/**
 * How a failed personal-workspace bootstrap is described to the person it
 * happened to (#338).
 *
 * Bootstrap is the call that decides whether somebody has a workspace at all,
 * so a failure cannot be a `console.error` — a silent no-op leaves a dashboard
 * with nothing on it and no account of why. Both call sites attempt once per
 * identity per page load, so every message says how to try again: reloading
 * clears the attempt and calls the function afresh.
 */

/** SQLSTATE raised by `ensure_personal_workspace` for a missing profile row. */
export const PROFILE_MISSING = 'PW001';

export interface BootstrapFailure {
  message: string;
  description: string;
}

interface PostgrestLikeError {
  code?: string | null;
  message?: string | null;
}

export function describeBootstrapFailure(
  error: PostgrestLikeError | null | undefined,
): BootstrapFailure {
  const message = 'Your personal workspace could not be set up.';

  if (error?.code === PROFILE_MISSING) {
    return {
      message,
      description:
        'Your account is missing its profile record, so it cannot be added as a '
        + 'member. Reload to try again; if it keeps failing, contact support.',
    };
  }

  return {
    message,
    description: `${error?.message ?? 'The server gave no reason.'} Reload to try again.`,
  };
}
